'use client';

import React, { useState, useRef, useEffect } from 'react';
import { RangeCalendar, Button, ModalContent, ModalBody, ModalFooter } from '@heroui/react';
import { CalendarDate, parseDate, getLocalTimeZone, DateValue } from '@internationalized/date';
import { I18nProvider } from '@react-aria/i18n';
import { useTranslations } from '../../contexts/translation.context';
import { ResponsiveModal } from './ResponsiveModal';
import { useIsMobile } from '../../hooks/useMobile';

export interface DateFilterSelectProps {
  placeholder: string;
  selectedKey: string;
  onSelectionChange: (key: string) => void;
  onCustomDateChange?: (startDate: string, endDate: string) => void;
  startContent?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'campaign';
  theme?: 'client' | 'partner';
  customStartDate?: string;
  customEndDate?: string;
}

const DateFilterSelect: React.FC<DateFilterSelectProps> = ({
  placeholder,
  selectedKey,
  onSelectionChange,
  onCustomDateChange,
  startContent,
  className = '',
  disabled = false,
  size = 'sm',
  variant = 'default',
  theme = 'client',
  customStartDate,
  customEndDate,
}) => {
  const { t, lang } = useTranslations();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [localSelectedKey, setLocalSelectedKey] = useState(selectedKey); // Local state for dropdown selection
  const [selectedDateRange, setSelectedDateRange] = useState<{
    start: CalendarDate;
    end: CalendarDate;
  } | null>(
    customStartDate && customEndDate
      ? {
          start: parseDate(customStartDate),
          end: parseDate(customEndDate),
        }
      : null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left?: number;
    right?: number;
  }>({ top: 0 });

  // Sync local state with prop when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setLocalSelectedKey(selectedKey);
    }
  }, [isOpen, selectedKey]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = localSelectedKey === 'custom' ? 816 : 200; // 200 for options only, 816 for options + calendar
      const viewportWidth = window.innerWidth;

      // Check if dropdown would overflow on the right
      const wouldOverflowRight = rect.left + dropdownWidth > viewportWidth;
      // Check if dropdown would overflow on the left if aligned to right
      const wouldOverflowLeft = rect.right - dropdownWidth < 0;

      if (wouldOverflowRight && !wouldOverflowLeft) {
        // Align to right edge of button
        setDropdownPosition({
          top: rect.bottom + 4,
          right: viewportWidth - rect.right,
        });
      } else {
        // Align to left edge of button
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    }
  }, [isOpen, localSelectedKey]);

  const dateOptions = [
    { key: 'today', label: t('statistics.filters.today') },
    { key: 'week', label: t('statistics.filters.week') },
    { key: 'month', label: t('statistics.filters.month') },
    { key: 'quarter', label: t('statistics.filters.quarter') },
    { key: 'halfyear', label: t('statistics.filters.halfyear') },
    { key: 'year', label: t('statistics.filters.year') },
    { key: 'all', label: t('statistics.filters.allTime') },
    { key: 'custom', label: t('statistics.filters.customRange') },
  ];

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: 'bg-auth-surface border-none rounded-2 !px-3 !min-h-9 !h-9',
          value: 'text-sm !text-auth-muted',
        };
      default:
        return {
          trigger:
            'h-9 bg-gray-100 border-none rounded-2 !px-3 hover:bg-gray-200 focus:border-blue-500',
          value: 'text-sm !text-auth-muted',
        };
    }
  };

  const variantStyles = getVariantStyles();

  const getSelectedLabel = () => {
    const selectedOption = dateOptions.find((option) => option.key === selectedKey);
    return selectedOption ? selectedOption.label : placeholder;
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);

      if (isOutsideButton && isOutsidePopover) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (key: string) => {
    if (key !== 'custom') {
      // For preset options, apply immediately
      onSelectionChange(key);
      setLocalSelectedKey(key);
      setIsOpen(false);
    } else {
      // For custom, just show the calendar without triggering parent update
      setLocalSelectedKey(key);
      // Reset date selection when opening custom date picker
      if (!customStartDate || !customEndDate) {
        setSelectedDateRange(null);
      }
      // Keep dropdown open for custom date selection
    }
  };

  const handleCustomDateApply = () => {
    if (selectedDateRange && onCustomDateChange) {
      const startDateString = selectedDateRange.start.toString();
      const endDateString = selectedDateRange.end.toString();
      // Only now trigger the parent update with custom dates
      onCustomDateChange(startDateString, endDateString);
      setIsOpen(false);
    }
  };

  const handleCustomDateCancel = () => {
    setSelectedDateRange(
      customStartDate && customEndDate
        ? {
            start: parseDate(customStartDate),
            end: parseDate(customEndDate),
          }
        : null
    );
    setLocalSelectedKey(selectedKey); // Reset to actual selected key
    setIsOpen(false);
  };

  const handleDateRangeChange = (value: any) => {
    if (value && value.start && value.end) {
      setSelectedDateRange({
        start: value.start as CalendarDate,
        end: value.end as CalendarDate,
      });
    } else if (value && value.start && !value.end) {
      // Только начальная дата выбрана
      setSelectedDateRange(null);
    }
  };

  const formatDisplayDate = () => {
    if (selectedKey === 'custom' && selectedDateRange) {
      const startDate = selectedDateRange.start.toDate(getLocalTimeZone());
      const endDate = selectedDateRange.end.toDate(getLocalTimeZone());
      const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
      return `${startDate.toLocaleDateString(locale)} - ${endDate.toLocaleDateString(locale)}`;
    }
    return getSelectedLabel();
  };

  return (
    <div className="relative w-fit" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          ${variantStyles.trigger}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          flex items-center gap-1.5 whitespace-nowrap ${className}
        `}
      >
        {startContent && <span className="flex-shrink-0">{startContent}</span>}
        <span
          className={
            selectedKey && selectedKey !== 'all' ? 'text-sm !text-auth-ink' : variantStyles.value
          }
        >
          {formatDisplayDate()}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </button>

      {isOpen && isMobile && (
        <ResponsiveModal isOpen={isOpen} onClose={() => setIsOpen(false)} scrollBehavior="inside">
          <ModalContent className="pt-8">
            <ModalBody className="px-4">
              <div className="bg-white">
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {dateOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => handleOptionSelect(option.key)}
                        className={`px-3 py-1.5 rounded-2 text-sm border ${
                          localSelectedKey === option.key
                            ? 'bg-[#eeeeef] text-auth-ink'
                            : 'border-[#e0e0e0] text-auth-ink'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {localSelectedKey === 'custom' && (
                  <>
                    <div className="-mx-4 self-center">
                      <div
                        className={`${theme === 'partner' ? 'partner-range-calendar' : ''}`}
                        style={
                          theme === 'partner'
                            ? ({
                                '--heroui-primary': '#006FEE',
                                '--nextui-primary': '#006FEE',
                                '--heroui-focus': '#006FEE',
                                '--nextui-focus': '#006FEE',
                              } as React.CSSProperties)
                            : undefined
                        }
                      >
                        <I18nProvider locale={lang === 'ru' ? 'ru-RU' : 'en-US'}>
                          <RangeCalendar
                            aria-label={t('statistics.filters.dateRange') || 'Date range'}
                            value={selectedDateRange}
                            onChange={handleDateRangeChange}
                            visibleMonths={1}
                            classNames={{
                              base: 'w-full m:pt-4 bg-transparent border-none shadow-none',
                              content: 'w-full border-none shadow-none p-0 flex flex-col',
                            }}
                          />
                        </I18nProvider>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ModalBody>
            <ModalFooter className="px-4">
              {/* Action buttons */}
              <div className="flex flex-col gap-3">
                {localSelectedKey === 'custom' && (
                  <div className="text-sm text-auth-ink leading-[1.125rem]">
                    {!selectedDateRange && (
                      <span className="text-gray-400">
                        {t('statistics.filters.selectDateRange') || 'Select date range'}
                      </span>
                    )}
                    {selectedDateRange &&
                      `${selectedDateRange.start.toDate(getLocalTimeZone()).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US')} - ${selectedDateRange.end.toDate(getLocalTimeZone()).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US')}`}
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="flat"
                    color="danger"
                    size="md"
                    className="bg-[rgba(243,18,96,0.2)] text-auth-strength-weak h-10 px-4 rounded-3"
                    onPress={handleCustomDateCancel}
                  >
                    {t('buttons.cancel') || 'Cancel'}
                  </Button>
                  <Button
                    className={`${theme === 'partner' ? 'bg-partner' : 'bg-primary'} text-white h-10 px-3.5 py-2.5 rounded-3`}
                    onPress={handleCustomDateApply}
                    isDisabled={!selectedDateRange}
                  >
                    {t('buttons.apply') || 'Apply'}
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </ModalContent>
        </ResponsiveModal>
      )}
      {isOpen && !isMobile && (
        <div
          ref={popoverRef}
          className="fixed bg-white rounded-3 shadow-[0px_0px_24px_0px_rgba(0,0,0,0.1)] border border-[#e0e0e0] z-[9999] overflow-hidden"
          style={{
            top: dropdownPosition.top,
            ...(dropdownPosition.left !== undefined ? { left: dropdownPosition.left } : {}),
            ...(dropdownPosition.right !== undefined ? { right: dropdownPosition.right } : {}),
          }}
        >
          <div className="flex">
            {/* Left side - Options */}
            <div className="w-[12.5rem] p-2 border-r border-[#e0e0e0]">
              {dateOptions.map((option) => (
                <div
                  key={option.key}
                  onClick={() => handleOptionSelect(option.key)}
                  className={`
                    flex items-center px-2 py-0 h-8 rounded-2 cursor-pointer hover:bg-[#eeeeef]
                    ${localSelectedKey === option.key ? 'bg-[#eeeeef]' : ''}
                  `}
                >
                  <span className="text-sm text-auth-ink leading-[1.125rem]">{option.label}</span>
                </div>
              ))}
            </div>

            {/* Right side - Calendar (only show when custom is selected) */}
            {localSelectedKey === 'custom' && (
              <div className="md:w-[38.5rem] bg-white">
                <div
                  className={`w-[38.5rem] h-[20.125rem] ${theme === 'partner' ? 'partner-range-calendar' : ''}`}
                  style={
                    theme === 'partner'
                      ? ({
                          '--heroui-primary': '#006FEE',
                          '--nextui-primary': '#006FEE',
                          '--heroui-focus': '#006FEE',
                          '--nextui-focus': '#006FEE',
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <I18nProvider locale={lang === 'ru' ? 'ru-RU' : 'en-US'}>
                    <RangeCalendar
                      aria-label={t('statistics.filters.dateRange') || 'Date range'}
                      value={selectedDateRange}
                      onChange={handleDateRangeChange}
                      visibleMonths={2}
                      classNames={{
                        base: 'w-[38.5rem] h-[20.125rem] pt-4 bg-transparent border-none shadow-none',
                        content:
                          'w-[38.5rem] h-[20.125rem] border-none shadow-none p-0 flex flex-col',
                      }}
                    />
                  </I18nProvider>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2 mt-[-1rem] px-6 pb-4 ">
                  <div className="flex-1 text-sm text-auth-ink leading-[1.125rem]">
                    {!selectedDateRange && (
                      <span className="text-gray-400">
                        {t('statistics.filters.selectDateRange') || 'Select date range'}
                      </span>
                    )}
                    {selectedDateRange &&
                      `${selectedDateRange.start.toDate(getLocalTimeZone()).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US')} - ${selectedDateRange.end.toDate(getLocalTimeZone()).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US')}`}
                  </div>
                  <Button
                    variant="flat"
                    color="danger"
                    size="md"
                    className="bg-[rgba(243,18,96,0.2)] text-auth-strength-weak h-10 px-4 rounded-3"
                    onPress={handleCustomDateCancel}
                  >
                    {t('buttons.cancel') || 'Cancel'}
                  </Button>
                  <Button
                    className={`${theme === 'partner' ? 'bg-partner' : 'bg-primary'} text-white h-10 px-3.5 py-2.5 rounded-3`}
                    onPress={handleCustomDateApply}
                    isDisabled={!selectedDateRange}
                  >
                    {t('buttons.apply') || 'Apply'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterSelect;
